import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Select, Tabs, Tooltip, type InputRef } from 'antd';
import { UploadBlue } from '@shared/assets/icons/components';
import { Attachments, type AttachmentsListRef } from '@shared/ui/attachments';
import { Text } from '@shared/ui/antd';
import { authSelectors } from '@shared/redux/ducks/auth';
import { useAppSelector } from '@shared/redux';
import {
  useKnowledgeLinkAddKnowledgeLinkAddPostMutation,
  useKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostMutation,
  useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation,
  type KnowledgeSyncTimeEnum,
} from '@shared/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './knowledge-base-empty-state.module.scss';
import {
  hasKnowledgeLinkProtocol,
  parseKnowledgeLink,
} from '@modules/knowledge-base/lib/parse-link';
import { KNOWLEDGE_SYNC_PERIODS } from '@modules/knowledge-base/lib/sync-periods';

type KnowledgeBaseSourceType = 'document' | 'link' | 'linked_document';

export const KnowledgeBaseEmptyState = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase.empty' });
  const { t: tKnowledgeBase } = useTranslation('translation', { keyPrefix: 'knowledgeBase' });
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const [sourceType, setSourceType] = useState<KnowledgeBaseSourceType>('document');
  const [attachmentsState, setAttachmentsState] = useState({
    fileCount: 0,
    isFormInvalid: false,
  });
  const [link, setLink] = useState('');
  const [linkedDocument, setLinkedDocument] = useState({
    url: '',
    syncPeriod: 'EVERY_DAY' as KnowledgeSyncTimeEnum,
    title: '',
    description: '',
  });
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isSavingLinkedDocument, setIsSavingLinkedDocument] = useState(false);
  const [addKnowledgeLink, { isLoading: isLinkUploading }] =
    useKnowledgeLinkAddKnowledgeLinkAddPostMutation();
  const [addLinkedDocument, { isLoading: isLinkedDocumentUploading }] =
    useKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostMutation();
  const [updateSyncPeriod, { isLoading: isSyncPeriodUpdating }] =
    useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation();
  const attachmentsListRef = useRef<AttachmentsListRef>(null);
  const linkInputRef = useRef<InputRef>(null);
  const isSaveDisabled = attachmentsState.fileCount === 0 || attachmentsState.isFormInvalid;
  const parsedLink = useMemo(() => parseKnowledgeLink(link), [link]);
  const parsedLinkedDocumentUrl = useMemo(
    () => parseKnowledgeLink(linkedDocument.url),
    [linkedDocument.url],
  );
  const showHttpsAddon = !hasKnowledgeLinkProtocol(link);
  const showLinkedDocumentHttpsAddon = !hasKnowledgeLinkProtocol(linkedDocument.url);
  const previousShowHttpsAddonRef = useRef(showHttpsAddon);
  const isLinkInvalid = link.trim().length > 0 && !parsedLink;
  const isLinkSaveDisabled = !parsedLink || !organizationId;
  const isLinkedDocumentUrlInvalid =
    linkedDocument.url.trim().length > 0 && !parsedLinkedDocumentUrl;
  const isLinkedDocumentSaveDisabled =
    !parsedLinkedDocumentUrl ||
    !organizationId ||
    !linkedDocument.title.trim() ||
    isSavingLinkedDocument;
  const syncPeriodOptions = useMemo(
    () =>
      KNOWLEDGE_SYNC_PERIODS.map((syncPeriod) => ({
        value: syncPeriod,
        label: tKnowledgeBase(`syncPeriods.${syncPeriod}`),
      })),
    [tKnowledgeBase],
  );

  useEffect(() => {
    const previousShowHttpsAddon = previousShowHttpsAddonRef.current;

    if (sourceType === 'link' && previousShowHttpsAddon !== showHttpsAddon) {
      linkInputRef.current?.focus({ cursor: 'end' });
    }

    previousShowHttpsAddonRef.current = showHttpsAddon;
  }, [showHttpsAddon, sourceType]);

  const handleSaveFile = async () => {
    if (!attachmentsListRef.current) return;
    setIsUploadingFiles(true);
    try {
      await attachmentsListRef.current.uploadAll();
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleSaveLink = async () => {
    if (!parsedLink || !organizationId) return;

    await addKnowledgeLink({
      bodyKnowledgeLinkAddKnowledgeLinkAddPost: {
        organization_id: Number(organizationId),
        url: parsedLink.url,
        title: parsedLink.host,
        description: parsedLink.url,
      },
    }).unwrap();

    setLink('');
  };

  const handleLinkedDocumentChange = (
    field: keyof typeof linkedDocument,
    value: string | KnowledgeSyncTimeEnum,
  ) => {
    setLinkedDocument((previousLinkedDocument) => ({
      ...previousLinkedDocument,
      [field]: value,
    }));
  };

  const handleSaveLinkedDocument = async () => {
    if (!parsedLinkedDocumentUrl || !organizationId) return;

    const title = linkedDocument.title.trim();
    const description = linkedDocument.description.trim();

    if (!title) return;

    setIsSavingLinkedDocument(true);
    try {
      const createdKnowledgeBase = await addLinkedDocument({
        bodyKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPost: {
          organization_id: Number(organizationId),
          url: parsedLinkedDocumentUrl.url,
          title,
          description,
        },
      }).unwrap();

      await updateSyncPeriod({
        bodyKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost: {
          knowledge_base_id: createdKnowledgeBase.id,
          sync_period: linkedDocument.syncPeriod,
        },
      }).unwrap();

      setLinkedDocument({
        url: '',
        syncPeriod: 'EVERY_DAY',
        title: '',
        description: '',
      });
    } finally {
      setIsSavingLinkedDocument(false);
    }
  };

  return (
    <Flex vertical align="center" justify="center" className={styles.root}>
      <Flex vertical gap={16} className={styles.content}>
        <Flex vertical gap={8} className={styles.header}>
          <Text variant="xl" fontWeight={600} className={styles.title}>
            {t('title')}
          </Text>
          <Text variant="sm" className={styles.subtitle}>
            {t('subtitle')}
          </Text>
        </Flex>

        <Tabs
          activeKey={sourceType}
          onChange={(key) => setSourceType(key as KnowledgeBaseSourceType)}
          className={styles.tabs}
          items={[
            { key: 'document', label: t('tabs.document') },
            { key: 'link', label: t('tabs.link') },
            { key: 'linked_document', label: t('tabs.linkedDocument') },
          ]}
        />

        {sourceType === 'document' && (
          <>
            <Text variant="sm" className={styles.limit}>
              {t('limit')}
            </Text>

            <Attachments.Root>
              <div className={styles.attachments}>
                <Attachments.UploadInput
                  variant="knowledgeBase"
                  icon={<UploadBlue />}
                  title={t('attachTitle')}
                  subtitle={t('attachSubtitle')}
                  maxFiles={5}
                />
                <Attachments.List
                  ref={attachmentsListRef}
                  onStateChange={setAttachmentsState}
                  isUploadButtonHidden
                  className={styles.attachmentsList}
                />
              </div>
            </Attachments.Root>
          </>
        )}

        {sourceType === 'link' && (
          <Flex vertical gap={8} className={styles.linkSection}>
            <Input
              ref={linkInputRef}
              value={link}
              onChange={(event) => setLink(event.target.value)}
              placeholder={t('linkPlaceholder')}
              className={styles.linkInput}
              addonBefore={showHttpsAddon ? 'https://' : undefined}
              status={isLinkInvalid ? 'error' : undefined}
            />
            {isLinkInvalid && (
              <Text variant="xs" className={styles.linkError}>
                {t('linkInvalid')}
              </Text>
            )}
          </Flex>
        )}

        {sourceType === 'linked_document' && (
          <Flex vertical gap={16} className={styles.linkSection}>
            <Flex vertical gap={6}>
              <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                {t('linkedDocument.urlLabel')}
                <Tooltip title={t('linkedDocument.urlTooltip')}>
                  <QuestionCircleOutlined className={styles.tooltipIcon} />
                </Tooltip>
              </Text>
              <Input
                value={linkedDocument.url}
                onChange={(event) => handleLinkedDocumentChange('url', event.target.value)}
                placeholder={t('linkedDocument.urlPlaceholder')}
                className={styles.linkInput}
                addonBefore={showLinkedDocumentHttpsAddon ? 'https://' : undefined}
                status={isLinkedDocumentUrlInvalid ? 'error' : undefined}
              />
              {isLinkedDocumentUrlInvalid && (
                <Text variant="xs" className={styles.linkError}>
                  {t('linkInvalid')}
                </Text>
              )}
            </Flex>

            <Flex vertical gap={6}>
              <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                {t('linkedDocument.syncPeriodLabel')}
              </Text>
              <Select
                value={linkedDocument.syncPeriod}
                options={syncPeriodOptions}
                onChange={(value) => handleLinkedDocumentChange('syncPeriod', value)}
                className={styles.syncPeriodSelect}
              />
            </Flex>

            <Flex vertical gap={6}>
              <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                {t('linkedDocument.titleLabel')}
              </Text>
              <Input
                value={linkedDocument.title}
                onChange={(event) => handleLinkedDocumentChange('title', event.target.value)}
                placeholder={t('linkedDocument.titlePlaceholder')}
                className={styles.linkInput}
              />
            </Flex>

            <Flex vertical gap={6}>
              <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                {t('linkedDocument.descriptionLabel')}
              </Text>
              <Input.TextArea
                value={linkedDocument.description}
                onChange={(event) => handleLinkedDocumentChange('description', event.target.value)}
                placeholder={t('linkedDocument.descriptionPlaceholder')}
                className={styles.descriptionInput}
                maxLength={2048}
                showCount
                rows={4}
              />
            </Flex>
          </Flex>
        )}

        {sourceType === 'document' && attachmentsState.fileCount > 0 && (
          <Button
            type="primary"
            className={styles.saveButton}
            onClick={handleSaveFile}
            loading={isUploadingFiles}
            disabled={isSaveDisabled || isUploadingFiles}
            block
          >
            {t('button')}
          </Button>
        )}

        {sourceType === 'link' && (
          <Button
            type="primary"
            className={styles.saveButton}
            onClick={handleSaveLink}
            loading={isLinkUploading}
            disabled={isLinkSaveDisabled || isLinkUploading}
            block
          >
            {t('button')}
          </Button>
        )}

        {sourceType === 'linked_document' && (
          <Button
            type="primary"
            className={styles.saveButton}
            onClick={handleSaveLinkedDocument}
            loading={isSavingLinkedDocument || isLinkedDocumentUploading || isSyncPeriodUpdating}
            disabled={
              isLinkedDocumentSaveDisabled || isLinkedDocumentUploading || isSyncPeriodUpdating
            }
            block
          >
            {t('button')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
